import type { ReactNode } from "react";
import { Link } from "wouter";
import { ChevronRight, type LucideIcon } from "lucide-react";
import cx from "classnames";

interface AccountRowProps {
  icon: LucideIcon;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Navigates when set; otherwise the row is a button calling `onClick`. */
  href?: string;
  onClick?: () => void;
  tone?: "default" | "danger";
}

/**
 * One tappable row on the account screens: tinted icon, title, optional
 * supporting line, chevron. Either a link to a secondary screen or a button.
 */
export function AccountRow({
  icon: Icon,
  title,
  subtitle,
  href,
  onClick,
  tone = "default",
}: AccountRowProps) {
  const danger = tone === "danger";
  const className = "group w-full flex flex-row items-center gap-3 p-4 card text-left";

  const content = (
    <>
      <span
        className={cx(
          "w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0",
          danger
            ? "bg-red-100 dark:bg-red-900/30"
            : "bg-primary-100 dark:bg-primary-900/40",
        )}
      >
        <Icon
          className={cx(
            "w-5 h-5",
            danger
              ? "text-red-600 dark:text-red-400"
              : "text-primary-600 dark:text-primary-400",
          )}
        />
      </span>
      <span className="min-w-0 flex-1 flex flex-col">
        <span
          className={cx(
            "font-semibold text-base leading-tight",
            danger ? "text-red-600 dark:text-red-400" : "dark:text-neutral-100",
          )}
        >
          {title}
        </span>
        {subtitle != null && (
          <span className="text-xs text-dark-muted dark:text-neutral-400 truncate">
            {subtitle}
          </span>
        )}
      </span>
      <ChevronRight
        className={cx(
          "w-5 h-5 text-neutral-400 transition-colors flex-shrink-0",
          danger ? "group-hover:text-red-500" : "group-hover:text-primary-500",
        )}
      />
    </>
  );

  if (href) {
    return (
      <Link href={href} className={className}>
        {content}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} className={className}>
      {content}
    </button>
  );
}
